import { BaseHttpService } from 'common/services/BaseHttpService';
import { OAuthHandler } from 'common/handlers/auth/OAuthHandler';
import { serialize } from 'common/helpers/StringSerialize';
import { LEAP, HTTP } from './common/constants';
import endpoints from './common/endpoints';
import {
	LEAP_AUTH_CLIENT_REDIRECT_URI,
	LEAP_AUTH_CLIENT_SECRET,
	LEAP_AUTH_CLIENT_SCOPE,
	LEAP_AUTH_CLIENT_ID
} from 'assets/configurations/envConfigs';


const { IDENTIFIERS, GRANT_TYPE } = LEAP.AUTH;
const { AUTHORIZATION_CODE, REFRESH_TOKEN } = GRANT_TYPE;
const { HEADER, TOKEN_TYPE } = IDENTIFIERS;
const { NAME, CONTENT_TYPE } = HTTP.HEADERS;



export class HttpService extends BaseHttpService{

    constructor(otpions = {}) {
		super({ ...otpions, endpoints });
		return this;
	}


    set initOAuthHandler ({
		removeClientCredentials,
		setClientCredentials,
		getClientCredentials,
		destroySession
	}) {
        if(!this._oAuthHandler) {
            this._oAuthHandler = new OAuthHandler({
                redirectUri: LEAP_AUTH_CLIENT_REDIRECT_URI,
                clientSecret: LEAP_AUTH_CLIENT_SECRET,
                clientId: LEAP_AUTH_CLIENT_ID,
                scope: LEAP_AUTH_CLIENT_SCOPE,
                removeClientCredentials,
                setClientCredentials,
                getClientCredentials,
                destroySession,
                service: this
            });
        }

        this._oAuthHandler.syncReactState({
            removeClientCredentials,
            setClientCredentials,
            getClientCredentials,
            destroySession
        });

		/*return this._oAuthHandler || (this._oAuthHandler = new OAuthHandler({
			redirectUri: LEAP_AUTH_CLIENT_REDIRECT_URI,
			clientSecret: LEAP_AUTH_CLIENT_SECRET,
            clientId: LEAP_AUTH_CLIENT_ID,
            scope: LEAP_AUTH_CLIENT_SCOPE,
			removeClientCredentials,
			setClientCredentials,
			getClientCredentials,
			destroySession,
			service: this
        }));*/
	}

    get oAuthHandler() {
		return this._oAuthHandler;
	}

	get accessToken() {
		return this.oAuthHandler.getCredentials().then(credentials => credentials['access_token']);
	}

	logout() {
		return this.oAuthHandler.logout();
	}

	// #region LEAP OAUTH
	getOauth2AuthorizationUrl(options) {
		return this.getUrl('oauth2_authorization', options);
	}

	accessTokenOrRevalidateOptions(grantType, credentials) {
		return {
			headers: {
				[NAME.CONTENT_TYPE]: CONTENT_TYPE.FORM_URL_ENCODED_UTF8
			},
			body: serialize({ object: { ...grantType, ...credentials } })
		}
	}

	async grantAccessToken(code, oAuthClientCredentials) {
		const response = await fetch(this.getUrl('oauth2_token'), {
			method: "POST",
			...this.accessTokenOrRevalidateOptions({
				'grant_type': AUTHORIZATION_CODE,
				'code': code
			}, oAuthClientCredentials)
		});
		const body = await response.json();

		return { body };
	}

	async revalidateAccessToken(refreshToken, oAuthClientCredentials) {
		const response = await fetch(this.getUrl('oauth2_token'), {
			method: "POST",
			...this.accessTokenOrRevalidateOptions({
				'grant_type': REFRESH_TOKEN,
				'refresh_token': refreshToken
			}, oAuthClientCredentials)
		});
		const body = await response.json();

		return { body };
	}

	async fetchStateIframeURL() {
		const response = await fetch(this.getUrl('oauth2_discover'));
		const body = await response.json();

		return { body };
	}

	createStateIframe(url) {
		const iframe = document.createElement('iframe');
		iframe.src = url;
		iframe.style.cssText = `
			position: absolute;
			border: none;
			z-index: -1;
			height: 0;
			width: 0;
			left: 0;
			top: 0
		`;
		return iframe;
	}
	// #endregion

	// #region LEAP ACCOUNT
	async fetchMe() {
		const url = this.getUrl('me');
		const response = await this.accessToken.then(token => ({
			[HEADER]: `${TOKEN_TYPE} ${token}`
		})).then(SecurityHeaders => fetch(url, {
			headers: SecurityHeaders
		}));
		const body = await response.json();

		return { body };
	}
	// #endregion

	// #region LEAP DRM
	async getRights({ rights = [] }) {
		const url = this.getUrl('drm_check');

		const response = await this.accessToken.then(token => ({
			[HEADER]: `${TOKEN_TYPE} ${token}`
		})).then(SecurityHeaders => fetch(url, {
			method: "POST",
			headers: {
				[NAME.CONTENT_TYPE]: CONTENT_TYPE.JSON,
				...SecurityHeaders
			},
			body: JSON.stringify(rights)
		}));
		const body = await response.json();

		return { body };
	}
	// #endregion

	// #region ORFF_HERO
	async setGameUser({ gameId, userId, username }) {
		const url = this.getUrl('set_user', { gameId, userId });

		const response = await this.accessToken.then(token => ({
			[HEADER]: `${TOKEN_TYPE} ${token}`
		})).then(SecurityHeaders => fetch(url, {
			method: "POST",
			headers: {
				[NAME.CONTENT_TYPE]: CONTENT_TYPE.JSON,
				...SecurityHeaders
			},
			body: JSON.stringify({
				'username': username,
				'user_id': userId
			})
		}));
		const body = await response.json();

		return { body };
	}

	async fetchUserScore({ gameId, userId }) {
		const url = this.getUrl('user_score', { gameId, userId });

		const response = await this.accessToken.then(token => ({
			[HEADER]: `${TOKEN_TYPE} ${token}`
		})).then(SecurityHeaders => fetch(url, {
			headers: {
				...SecurityHeaders
			}
		}));
		const body = await response.json();

		return body;
	}

    async setUserScore({
        gameId,
        userId,
        musicId,
        musicName,
        instrumentId,
        instrumentName,
        points,
        progress }) {
		const url = this.getUrl('user_score', { gameId, userId });

		const response = await this.accessToken.then(token => ({
			[HEADER]: `${TOKEN_TYPE} ${token}`
		})).then(SecurityHeaders => fetch(url, {
            method: "PUT",
			headers: {
                [NAME.CONTENT_TYPE]: CONTENT_TYPE.JSON,
				...SecurityHeaders
			},
			body: JSON.stringify({
				'music_id': musicId,
				'music_name': musicName,
				'instrument_id': instrumentId,
				'instrument_name': instrumentName,
				'evaluation': progress,
				'points': points
			})
		}));
		const body = await response.json();

		return body;
	}

    async fetchUserBattles({ gameId, userId }) {
		const url = this.getUrl('user_battles', { gameId, userId });

		const response = await this.accessToken.then(token => ({
			[HEADER]: `${TOKEN_TYPE} ${token}`
		})).then(SecurityHeaders => fetch(url, {
			headers: {
				...SecurityHeaders
			}
		}));
		const body = await response.json();

		return body;
	}

	async setUserBattles({
        gameId,
        userId,
        musicId,
        musicName,
        instrumentId,
        instrumentName,
        totalPlayers,
        points,
        progress,
        position,
        winner,
        scoreBoard }) {
		const url = this.getUrl('user_battles', { gameId, userId });

		const response = await this.accessToken.then(token => ({
			[HEADER]: `${TOKEN_TYPE} ${token}`
		})).then(SecurityHeaders => fetch(url, {
            method: "PUT",
			headers: {
                [NAME.CONTENT_TYPE]: CONTENT_TYPE.JSON,
				...SecurityHeaders
			},
			body: JSON.stringify({
				'music_id': musicId,
				'music_name': musicName,
				'instrument_id': instrumentId,
				'instrument_name': instrumentName,
                'total_players': totalPlayers,
				'evaluation': progress,
				'points': points,
                'position': position,
                'winner': winner,
                'scoreboard': scoreBoard
			})
		}));
		const body = await response.json();

		return body;
	}

	async fetchUserSettings({ gameId, userId }) {
		const url = this.getUrl('user_settings', { gameId, userId });

		const response = await this.accessToken.then(token => ({
			[HEADER]: `${TOKEN_TYPE} ${token}`
		})).then(SecurityHeaders => fetch(url, {
			headers: {
				...SecurityHeaders
			}
		}));
		const body = await response.json();

		return body;
	}

	async setUserSettings(gameId, reqBody = {}) {
		const url = this.getUrl('user_settings', { gameId, userId: reqBody?.userId });

		const response = await this.accessToken.then(token => ({
			[HEADER]: `${TOKEN_TYPE} ${token}`
		})).then(SecurityHeaders => fetch(url, {
            method: "PUT",
			headers: {
                [NAME.CONTENT_TYPE]: CONTENT_TYPE.JSON,
				...SecurityHeaders
			},
			body: JSON.stringify(reqBody)
		}));
		const body = await response.json();

		return body;
	}
	// #endregion

    async sendEvent(gameId, reqBody = {}) {
		const url = this.getUrl('game_events', { gameId });

		const response = await this.accessToken.then(token => ({
			[HEADER]: `${TOKEN_TYPE} ${token}`
		})).then(SecurityHeaders => fetch(url, {
            method: "POST",
			headers: {
                [NAME.CONTENT_TYPE]: CONTENT_TYPE.JSON,
				...SecurityHeaders
			},
			body: JSON.stringify(reqBody)
		}));
		const body = await response.json();

		return body;
	}
};

export default new HttpService();